import Konva from 'konva'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Image } from 'react-konva'

type Dimensions = { width: number; height: number }

function isPositive(value: number | undefined | null): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

function scaleDimension(
  width: number | undefined,
  height: number | undefined,
  newWidth: number | null = null,
  newHeight: number | null = null
): Dimensions {
  if (!isPositive(width) || !isPositive(height)) {
    return {
      width: 0,
      height: 0,
    }
  }

  if (isPositive(newWidth) && newHeight === null) {
    let ratio = newWidth / width
    return {
      width: width * ratio,
      height: height * ratio,
    }
  }

  if (isPositive(newHeight) && newWidth === null) {
    let ratio = newHeight / height
    return {
      width: width * ratio,
      height: height * ratio,
    }
  }

  return {
    width,
    height,
  }
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new window.Image()
    image.crossOrigin = 'Anonymous'
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', () => reject(new Error(`Could not decode image ${src.slice(0, 100)}`)))
    image.src = src
  })
}

async function fetchSvgText(src: string) {
  const response = await fetch(src)
  if (!response.ok) {
    throw new Error(`${src} returned ${response.status} ${response.statusText}`)
  }

  // A single page app rewrite answers any path with no file behind it by
  // serving index.html with a 200, so the status alone proves nothing
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('svg')) {
    throw new Error(`${src} returned ${contentType || 'an unknown content type'} rather than an SVG`)
  }

  return response.text()
}

function viewBoxDimensions(svgText: string): Dimensions | null {
  const viewBox = /viewBox\s*=\s*["']\s*[-\d.eE+]+[\s,]+[-\d.eE+]+[\s,]+([-\d.eE+]+)[\s,]+([-\d.eE+]+)/.exec(svgText)
  if (!viewBox) {
    return null
  }

  const width = Number(viewBox[1])
  const height = Number(viewBox[2])
  return isPositive(width) && isPositive(height) ? { width, height } : null
}

function intrinsicDimensions(image: HTMLImageElement): Dimensions | null {
  const { naturalWidth, naturalHeight } = image
  return isPositive(naturalWidth) && isPositive(naturalHeight) ? { width: naturalWidth, height: naturalHeight } : null
}

type Props = {
  src: string
  width?: number
  height?: number
  onLoaded?: (img: HTMLImageElement, ref: Konva.Image | null) => void
  stroke?: string
  strokeWidth?: number
} & Omit<Konva.ImageConfig, 'image'>

/**
 * Image component with support to image src support
 */
export const ImageURL = ({ src, onLoaded, width, height, ...imageProps }: Props) => {
  const [image, setImage] = useState<HTMLImageElement | null>(null)
  const [dimensions, setDimensions] = useState<Dimensions | null>(null)
  const imageRef = useRef<Konva.Image | null>(null)

  useEffect(() => {
    let cancelled = false
    const isSvg = src.endsWith('.svg')
    let svgText: string | null = null

    async function readSvgText() {
      if (svgText === null) {
        svgText = await fetchSvgText(src)
      }
      return svgText
    }

    async function load() {
      let loaded: HTMLImageElement

      try {
        loaded = await loadImage(src)
      } catch (error) {
        if (!isSvg) {
          console.error('Image failed to load:', src, error)
          return
        }

        try {
          const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(await readSvgText())}`
          loaded = await loadImage(dataUrl)
        } catch (retryError) {
          console.error('SVG failed to load:', src, retryError)
          return
        }
      }

      // An SVG sized in percentages has no intrinsic size, so scale it by its viewBox instead
      let size = intrinsicDimensions(loaded)
      if (!size && isSvg) {
        try {
          size = viewBoxDimensions(await readSvgText())
        } catch (error) {
          console.error('Could not read SVG dimensions:', src, error)
        }
      }

      if (!size) {
        console.error('Image reported no usable dimensions and will not render:', src)
        return
      }

      if (cancelled) {
        return
      }

      setImage(loaded)
      setDimensions(size)
      onLoaded?.(loaded, imageRef.current)
    }

    load()

    return () => {
      cancelled = true
    }
  }, [src, onLoaded])

  const size = useMemo(() => {
    return scaleDimension(dimensions?.width, dimensions?.height, width, height)
  }, [dimensions, width, height])

  return image ? (
    <Image {...imageProps} image={image} width={size.width} height={size.height} ref={imageRef as any} />
  ) : null
}
